import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { MANIFEST } from './entity_search.m';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('ENTITY_SEARCH');

export const entitySearchCommand: SelfContainedCommand = {
  id: MANIFEST.name,
  description: MANIFEST.description,
  autonomyLevel: 'auto',
  visualTreatment: 'result',

  schema: {
    parameters: [
      {
        name: 'query',
        type: 'string',
        description: 'Topic, concept, phrase, or question to search for in entity content.',
        required: true,
      },
      {
        name: 'entityTypes',
        type: 'array',
        description: 'Optional list of entity types to restrict results to.',
        required: false,
      },
      {
        name: 'semantic',
        type: 'boolean',
        description: 'Enable vector similarity re-ranking for conceptual matches.',
        required: false,
        defaultValue: false,
      },
      {
        name: 'limit',
        type: 'number',
        description: 'Maximum results to return (default 20).',
        required: false,
        defaultValue: 20,
      },
    ],
  },

  handler: async (
    params: {
      query: string;
      entityTypes?: string[];
      semantic?: boolean;
      limit?: number;
    },
    context: CommandExecutionContext,
  ): Promise<StandardToolResult> => {
    const { query, entityTypes, semantic = false, limit = 20 } = params;
    const t0 = Date.now();

    if (!query || !query.trim()) {
      return createErrorResult(
        'entity_search requires a non-empty query',
        'Provide a query string describing what to search for.',
        'validation',
      );
    }

    try {
      const { entityService } = await import('../../services/entityService');
      const results = await entityService.searchEntities(
        query.trim(),
        entityTypes,
        Math.min(limit, 50),
        semantic,
      );

      const elapsed = Date.now() - t0;
      log.info('[entity_search] completed', {
        query: query.trim(),
        entityTypes,
        semantic,
        resultCount: results.length,
        ms: elapsed,
      });

      if (results.length === 0) {
        return createSuccessResult(
          `No results found for "${query}"`,
          { results: [], count: 0, query },
        );
      }

      return createSuccessResult(
        `Found ${results.length} result${results.length !== 1 ? 's' : ''} for "${query}"`,
        {
          results,
          count: results.length,
          query,
          semantic,
        },
      );
    } catch (error: any) {
      log.error('[entity_search] error', { query, error: error?.message });
      return createErrorResult(
        'entity_search failed',
        error?.message || 'Unknown error',
        'execution',
      );
    }
  },
};
